import { useMemo } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

export type TemplateWarningInfo =
  | {
      type?: string;
      variable?: string;
      message?: string;
    }
  | string;

export type TemplateVariableSummary = {
  known?: string[];
  unknown?: string[];
  detected?: string[];
};

type TemplateWarningsProps = {
  warnings?: TemplateWarningInfo[] | null;
  variables?: TemplateVariableSummary | null;
  className?: string;
};

const UNKNOWN_TOKEN_REGEX = /\{\{UNKNOWN:([^}]+)\}\}/gi;

export function TemplateWarnings({ warnings, variables, className }: TemplateWarningsProps) {
  const normalizedWarnings = useMemo(() => {
    if (!warnings || warnings.length === 0) return [];
    const seen = new Set<string>();
    return warnings
      .map((warning) => {
        if (typeof warning === "string") {
          return { type: "unknown_variable", variable: warning, message: undefined };
        }
        return warning;
      })
      .filter((warning): warning is NonNullable<ReturnType<typeof normalizeWarning>> => {
        const normalized = normalizeWarning(warning);
        if (!normalized) return false;
        const key = `${normalized.type}:${normalized.variable ?? normalized.message ?? ""}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [warnings]);

  const variableBuckets = useMemo(() => {
    const detected = new Set<string>();
    const unknown = new Set<string>();
    const known = new Set<string>();
    const meta = variables ?? undefined;
    (meta?.detected ?? []).forEach((token) => detected.add(token));
    (meta?.unknown ?? []).forEach((token) => {
      detected.add(token);
      unknown.add(token);
    });
    normalizedWarnings
      .map((warning) => warning.variable)
      .filter((token): token is string => Boolean(token))
      .forEach((token) => {
        detected.add(token);
        unknown.add(token);
      });
    (meta?.known ?? []).forEach((token) => {
      detected.add(token);
      if (!unknown.has(token)) {
        known.add(token);
      }
    });
    if (!known.size && detected.size) {
      detected.forEach((token) => {
        if (!unknown.has(token)) {
          known.add(token);
        }
      });
    }
    return {
      detected: Array.from(detected),
      known: Array.from(known),
      unknown: Array.from(unknown),
    };
  }, [variables, normalizedWarnings]);

  if (
    !normalizedWarnings.length &&
    !variableBuckets.detected.length &&
    !variableBuckets.known.length &&
    !variableBuckets.unknown.length
  ) {
    return null;
  }

  return (
    <section className={cn("aa-glass-card space-y-3 p-4", className)}>
      {normalizedWarnings.length > 0 && (
        <div className="flex gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-100">
          <AlertTriangle className="mt-1 h-4 w-4 text-rose-200" />
          <div>
            <p className="text-xs uppercase tracking-wide text-rose-200">Template warnings</p>
            <ul className="mt-1 space-y-1 text-xs">
              {normalizedWarnings.map((warning, index) => (
                <li key={`${warning.variable ?? "warning"}-${index}`}>
                  {warning.variable ? <strong className="text-rose-100">{warning.variable}</strong> : null}
                  {warning.variable && warning.message ? ": " : ""}
                  <span className="text-rose-100">
                    {warning.message ||
                      (warning.type === "deprecated_syntax"
                        ? "Deprecated placeholder syntax detected."
                        : "Unknown variable detected in the rendered output.")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {variableBuckets.unknown.length > 0 && (
          <VariableList label="Unknown variables" tokens={variableBuckets.unknown} tone="danger" />
        )}
        {variableBuckets.known.length > 0 && (
          <VariableList label="Known variables" tokens={variableBuckets.known} tone="success" />
        )}
      </div>

      {variableBuckets.detected.length > 0 && (
        <details className="group rounded-xl border border-border/70 bg-background/40 px-3 py-2 text-sm text-foreground/70">
          <summary className="flex cursor-pointer items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
            <ChevronDown className="h-3 w-3 transition group-open:rotate-180" />
            Detected tags ({variableBuckets.detected.length})
          </summary>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {variableBuckets.detected.map((token) => (
              <code
                key={token}
                className="rounded-full border border-border/70 bg-background/30 px-2 py-0.5 text-[11px] text-foreground/80"
              >
                [{token}]
              </code>
            ))}
          </div>
        </details>
      )}
    </section>
  );
}

function VariableList({
  label,
  tokens,
  tone,
}: {
  label: string;
  tokens: string[];
  tone: "danger" | "success";
}) {
  if (!tokens.length) return null;
  const styles =
    tone === "danger"
      ? "border-rose-500/40 bg-rose-500/10 text-rose-100"
      : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100";
  const badgeStyles = tone === "danger" ? "bg-rose-500/20 text-rose-100" : "bg-emerald-500/20 text-emerald-100";
  return (
    <div className={cn("rounded-xl border px-3 py-2 text-sm", styles)}>
      <p className="text-[11px] uppercase tracking-wide">{label}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {tokens.map((token) => (
          <Badge
            key={`${label}-${token}`}
            variant="outline"
            className={cn("border-transparent", badgeStyles)}
          >
            [{token}]
          </Badge>
        ))}
      </div>
    </div>
  );
}

function normalizeWarning(warning: Exclude<TemplateWarningInfo, string>) {
  if (!warning) return null;
  const { variable, type, message } = warning;
  if (!variable && !message) {
    return null;
  }
  return {
    variable,
    type: type ?? "unknown_variable",
    message,
  };
}

export function extractUnknownPlaceholders(value?: string | null): string[] {
  if (!value) return [];
  const matches = new Set<string>();
  let match: RegExpExecArray | null;
  while ((match = UNKNOWN_TOKEN_REGEX.exec(value)) !== null) {
    const token = match[1]?.trim();
    if (token) {
      matches.add(token);
    }
  }
  return Array.from(matches);
}

export function highlightUnknownPlaceholders(html: string | undefined | null): string {
  if (!html) return "";
  return html.replace(UNKNOWN_TOKEN_REGEX, (_, variable) => {
    const token = String(variable).trim();
    return `<mark class="rounded bg-rose-200/70 px-1 font-semibold text-rose-900" data-variable="${token}">${token}</mark>`;
  });
}

export function highlightUnknownPlaintext(text: string | undefined | null): string {
  if (!text) return "";
  return highlightUnknownPlaceholders(escapeHtml(text));
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
